// ─────────────────────────────────────────────
//  orientation — "Josh Larson 5 Orient Sam Gongora" is TWO PEOPLE IN ONE ROOM.
//
//  THE RULE (product owner, an anesthesiologist, 2026-08-17): "when somebody is
//  orienting somebody, both CRNAs are in the same room." The preceptor runs the
//  case; the orientee is with them, learning, in that same room.
//
//  ⚠️ THIS CELL HAS ALREADY COST A LIVE INCIDENT. Until now the whole string was
//  taken as ONE provider name, which matched no staff record, so the row had a
//  name and no id. On 2026-08-14 `isSoloCase` read that missing id as an empty
//  seat and pulled Jessica Huang — supervising WOR 7, 8 and 12 — out of the
//  3 PM supervising pool; the board read "Just enough" when one doctor was
//  spare, and the room card claimed she was alone with the patient. See
//  domain/isSoloCase.test.js. The seat holds two people and the data model held
//  one.
//
//  ⚠️ THE SEAT IS THE PRECEPTOR. An orientee is not independent cover: they do
//  not relieve anyone, they do not hold the room on their own, and the ratio
//  must not count them as staffing. What they ARE is present — so they must
//  stop showing as free on the bench, and they must appear on the break board
//  for the room they are standing in.
//
//  Pure: no Firebase, no React, no importer types. The sheet parser writes the
//  structured fields; `orientationOf` re-derives them from a legacy `ahpName`
//  so boards imported BEFORE this shipped still read correctly without a
//  re-import.
// ─────────────────────────────────────────────
import {
  personSegmentIssue,
  stripDanglingRoutingWords,
  stripRoutingNote,
} from "./personSegment";

// "Orient", "Orienting", "orients", "ORIENT W/" — the separator as the sheet
// actually writes it, tolerant of case and a trailing "w/" or "with".
//
// ⚠️ A SLASH IS A SEPARATOR TOO, AND REQUIRING WHITESPACE HID A WHOLE PAIR.
// On 2026-09-07 the live board wrote "Rebecca Sigler 3 to TEE/Orient Cheryl
// Martin" — the character in front of "Orient" is "/", so this pattern did not
// match, the cell stayed ONE string, it matched no staff record, and Cheryl
// Martin appeared on no screen. The sheet already used the slash on the RIGHT
// of the word ("orients w/ Sam Gongora"); it uses it on the left as well.
const ORIENT_RE = /(?:\s+|\s*\/\s*)orient(?:s|ing)?\b(?:\s*(?:w\/|with))?\s+/i;

/**
 * The RAW two halves either side of the word "orient", plus the verdict on
 * whether they are two PEOPLE.
 *
 * ⚠️ THE WORD IS NOT THE RELATIONSHIP. On the live board 2026-08-20:
 *
 *     "Rebecca Sigler 3 orient Brianna Michaud 3"   two people
 *     "Olivia Meitzner 3 Orient to OB floor"        ONE person and a note
 *
 * `orient` is a separator in the first and an ordinary English verb in the
 * second, and the only reliable discriminator is whether what follows it is
 * PERSON-SHAPED. That test lives in domain/personSegment and every path asks
 * it — this one used to be the exception, which is exactly how a colleague
 * called `to OB floor` reached `partitionNewStaff` as a creatable person.
 *
 * The orientee floor is ONE word on purpose: the sheet really writes
 * "Fernanda Sa 3 orient Olivia", and pt.156 pinned that no rule may require
 * two words of the second person.
 *
 * @returns {null | { preceptor, orientee, tail, issue }}
 *   `issue` is null for a real pair, else the reason the split was refused.
 */
export function orientationParts(cell) {
  const text = String(cell || "").trim();
  if (!text) return null;
  const m = text.match(ORIENT_RE);
  if (!m) return null;
  // ⚠️ A DANGLING ROUTING WORD IS NOT A ROUTING NOTE. "maria mckay 5 to orient
  // adam batz" (live, WC IR, 2026-08-24) split correctly and was then refused
  // because the first half ends in "to" — so the pair collapsed, Maria was
  // stored as raw text with no id, and Adam Batz disappeared from the board.
  // Stripped only at the END and only whole tokens, so "3 to/from CIE6" and
  // every other real routing note still fails closed exactly as before.
  const preceptor = stripDanglingRoutingWords(text.slice(0, m.index).trim());
  const orientee = stripDanglingRoutingWords(text.slice(m.index + m[0].length).trim());
  // Both halves must name somebody. "Orient" alone, or a cell that merely ends
  // with the word, is not a pair — and inventing one would put a phantom person
  // in a room.
  if (!preceptor || !orientee) return null;
  // ⚠️ ASK THE PERSON TEST ABOUT THE NAME, NOT ABOUT THE JOURNEY. A preceptor
  // routed elsewhere — "Rebecca Sigler 3 to TEE" — contains the word "to", and
  // testing the raw half refused every such pair outright: the cell collapsed
  // to one unmatched string and the orientee vanished. `stripRoutingNote` is
  // the sheet reader's OWN annotation rule, shared rather than re-spelled, so
  // what is judged here is exactly what the reader will keep as the name.
  //
  // The RAW halves are still what we return — the reader needs the annotation
  // to recover `toRoom` / `fromRoom`.
  const issue =
    personSegmentIssue(stripRoutingNote(preceptor).text, { minWords: 2 }) ||
    personSegmentIssue(stripRoutingNote(orientee).text, { minWords: 1 });
  return { preceptor, orientee, tail: text.slice(m.index).trim(), issue: issue || null };
}

// Split a provider cell into the two people it names.
//
// Returns null when the cell names nobody in an orienting relationship, so a
// caller can keep its existing single-provider path untouched — the common case
// stays byte-identical. It ALSO returns null when the text after "orient" is
// not a person (see orientationParts): a routing note must never become a
// colleague.
//
// The shift code between the names ("Josh Larson 5 Orient Sam Gongora") is left
// ON the preceptor segment: the sheet parser's existing trailing-shift-code
// logic strips it, and duplicating that here would be a second definition of
// what a shift code looks like.
export function parseOrientation(cell) {
  const parts = orientationParts(cell);
  if (!parts || parts.issue) return null;
  return { preceptor: parts.preceptor, orientee: parts.orientee };
}

// True when this cell names an orienting pair.
export const isOrientingCell = (cell) => parseOrientation(cell) !== null;

// A trailing shift number left on a LEGACY preceptor name: "Josh Larson 5".
//
// ⚠️ NARROW ON PURPOSE — bare digits only, and only at the very end. The sheet
// parser owns the real shift-code vocabulary (ranges, L-suffixes, A-start
// codes) and strips it at import time; re-implementing that here would be a
// second definition of what a shift code is, drifting the first time the sheet
// invents another one. This handles the one case the fallback path actually
// hits — the digits the live board shows beside a name — and leaves anything
// it does not recognise alone rather than guessing a name apart.
//
// A name that is ONLY digits is returned untouched: better an odd name than no
// name at all.
function stripTrailingShiftDigits(name) {
  const trimmed = String(name || "").trim();
  const m = trimmed.match(/^(.*\S)\s+\d{1,2}$/);
  return m ? m[1] : trimmed;
}

// The orientee on an assignment row, from the STRUCTURED fields when the row was
// written by a parser that knows about orientation, else re-derived from the raw
// `ahpName` of a row imported before that existed.
//
// ⚠️ THE FALLBACK IS WHAT MAKES TODAY'S BOARD WORK. Every row already in
// Firestore carries the mashed string and no orientee fields; without this, the
// feature would only appear on boards imported after the next sheet run — which
// on a live hospital board means "tomorrow, maybe".
export function orientationOf(assignment) {
  if (!assignment) return null;
  const name = String(assignment.orienteeName || "").trim();
  if (name) {
    return {
      preceptorName: String(assignment.ahpName || "").trim(),
      orienteeName: name,
      orienteeId: assignment.orienteeId || null,
    };
  }
  const parsed = parseOrientation(assignment.ahpName);
  if (!parsed) return null;
  return {
    preceptorName: stripTrailingShiftDigits(parsed.preceptor),
    orienteeName: parsed.orientee,
    // A legacy row has no id for either person — the whole cell matched nothing.
    orienteeId: null,
  };
}

// The name to show for the seat itself. A legacy row's `ahpName` still holds
// both names mashed together, so every surface that prints it would otherwise
// print the sentence; this returns just the person holding the room.
export function seatName(assignment) {
  const orienting = orientationOf(assignment);
  if (orienting) return orienting.preceptorName;
  return String(assignment?.ahpName || "").trim();
}
